import { memo, useState, useCallback, type ReactNode } from 'react';
import type { Player } from '@bangos/shared';
import { useT } from '../i18n';
import '../styles/screens.css';

interface LobbyScreenProps {
  roomCode: string;
  players: Player[];
  isHost: boolean;
  playerCount: number;
  isReady: boolean;
  isSpectator: boolean;
  teamAssignment: 'blue' | 'red' | null;
  onToggleReady: () => void;
  onStartGame: () => void;
  onLeaveRoom: () => void;
  onSubmitSpectrum: (left: string, right: string) => void;
}

function LobbyScreenInner({
  roomCode,
  players,
  isHost,
  playerCount, // eslint-disable-line @typescript-eslint/no-unused-vars
  isReady,
  isSpectator,
  teamAssignment,
  onToggleReady,
  onStartGame,
  onLeaveRoom,
  onSubmitSpectrum,
}: LobbyScreenProps): ReactNode {
  const t = useT();
  const [spectrumLeft, setSpectrumLeft] = useState('');
  const [spectrumRight, setSpectrumRight] = useState('');
  const [spectrumFormOpen, setSpectrumFormOpen] = useState(false);

  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const shareableLink = `${window.location.origin}?room=${roomCode}`;

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {
      // Fallback for non-HTTPS or older browsers
      const input = document.createElement('input');
      input.value = roomCode;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  }, [roomCode]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = shareableLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  }, [shareableLink]);

  const handleSpectrumSubmit = () => {
    const trimmedLeft = spectrumLeft.trim();
    const trimmedRight = spectrumRight.trim();
    if (!trimmedLeft || !trimmedRight) return;
    onSubmitSpectrum(trimmedLeft, trimmedRight);
    setSpectrumLeft('');
    setSpectrumRight('');
    setSpectrumFormOpen(false);
  };
  // Split players into teams, spectators, and unassigned
  const spectators = players.filter((p) => p.role === 'spectator');
  const activePlayers = players.filter((p) => p.role !== 'spectator');
  const needsMorePlayers = activePlayers.length < 2;
  const allReady = activePlayers.length >= 2 && activePlayers.every((p) => p.isReady);
  const remaining = 2 - activePlayers.length;

  const bluePlayers = players.filter((p) => p.team === 'blue');
  const redPlayers = players.filter((p) => p.team === 'red');
  const unassignedPlayers = players.filter((p) => p.team === null && p.role !== 'spectator');

  // Count connected non-spectator players for the waiting message
  const connectedPlayers = activePlayers.filter((p) => p.isConnected).length;

  return (
    <div className="screen-container">
      <div className="screen-header">
        <p className="screen-title">{t('lobby.roomCodeHeading')}</p>
        <div className="room-code-display">{roomCode}</div>
        <div className="share-actions">
          <button
            className="btn btn--small btn--secondary"
            onClick={handleCopyCode}
          >
            {copiedCode ? t('lobby.copied') : t('lobby.copyCode')}
          </button>
          <button
            className="btn btn--small btn--secondary"
            onClick={handleCopyLink}
          >
            {copiedLink ? t('lobby.copied') : t('lobby.copyLink')}
          </button>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
          {t('lobby.shareHint')}
        </p>
      </div>

      {/* Team rosters (shown after assignment) */}
      {(teamAssignment !== null || bluePlayers.length > 0 || redPlayers.length > 0) && (
        <div className="teams-grid">
          <div className="team-card team-card--blue">
            <h3 className="team-card-title team-card-title--blue">{t('lobby.blueTeam')}</h3>
            <ul className="team-list">
              {bluePlayers.length === 0 && (
                <li className="team-player-item team-player-item--empty">
                  {t('lobby.noPlayersYet')}
                </li>
              )}
              {bluePlayers.map((player) => (
                <li key={player.id} className="team-player-item">
                  <span
                    className={'ready-dot' + (player.isReady ? ' ready-dot--ready' : ' ready-dot--not-ready')}
                  />
                  {player.name}
                  {!player.isConnected && (
                    <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.25rem' }}>
                      {t('lobby.disconnected')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="team-card team-card--red">
            <h3 className="team-card-title team-card-title--red">{t('lobby.redTeam')}</h3>
            <ul className="team-list">
              {redPlayers.length === 0 && (
                <li className="team-player-item team-player-item--empty">
                  {t('lobby.noPlayersYet')}
                </li>
              )}
              {redPlayers.map((player) => (
                <li key={player.id} className="team-player-item">
                  <span
                    className={'ready-dot' + (player.isReady ? ' ready-dot--ready' : ' ready-dot--not-ready')}
                  />
                  {player.name}
                  {!player.isConnected && (
                    <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.25rem' }}>
                      {t('lobby.disconnected')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Players waiting to be assigned (only show after teams assigned) */}
      {(teamAssignment !== null || bluePlayers.length > 0 || redPlayers.length > 0) && unassignedPlayers.length > 0 && (
        <div className="unassigned-section">
          <p className="unassigned-title">
            {t('lobby.players')} ({connectedPlayers})
          </p>
          <ul className="unassigned-list">
            {unassignedPlayers.map((player) => (
              <li key={player.id} className="unassigned-item">
                <span
                  className={'ready-dot' + (player.isReady ? ' ready-dot--ready' : ' ready-dot--not-ready')}
                />
                {player.name}
                {!player.isConnected && (
                  <span style={{ fontSize: '0.75rem', color: '#999' }}>{t('lobby.disconnected')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Show player list when no team assignments exist yet */}
      {teamAssignment === null && bluePlayers.length === 0 && redPlayers.length === 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <ul className="team-list">
            {players.map((player) => (
              <li key={player.id} className="team-player-item">
                <span
                  className={'ready-dot' + (player.isReady ? ' ready-dot--ready' : ' ready-dot--not-ready')}
                />
                {player.name}
                {!player.isConnected && (
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>
                    {t('lobby.disconnected')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Spectators section */}
      {spectators.length > 0 && (
        <div className="unassigned-section">
          <p className="unassigned-title">
            {t('lobby.spectators')} ({spectators.length})
          </p>
          <ul className="unassigned-list">
            {spectators.map((player) => (
              <li key={player.id} className="unassigned-item">
                👁️ {player.name}
                {!player.isConnected && (
                  <span style={{ fontSize: '0.75rem', color: '#999' }}>{t('lobby.disconnected')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Waiting message */}
      {needsMorePlayers && (
        <div className="waiting-msg">
          {t('lobby.waitingPlayers', { remaining })}
        </div>
      )}

      {/* Action buttons */}
      <div className="actions-section">
        {!isSpectator && (
          <button
            className={'btn btn--full' + (isReady ? ' btn--success' : ' btn--secondary')}
            onClick={onToggleReady}
          >
            {isReady ? t('lobby.ready') : t('lobby.clickWhenReady')}
          </button>
        )}

        {isHost && !isSpectator && (
          <button
            className="btn btn--full btn--warning"
            onClick={onStartGame}
            disabled={needsMorePlayers || !allReady}
          >
            {needsMorePlayers
              ? t('lobby.waitingPlayers', { remaining })
              : !allReady
                ? t('lobby.waitingForReady')
                : t('lobby.startGame')}
          </button>
        )}
      </div>

      {/* Spectrum submission form */}
      <div className="spectrum-submission-section">
        <button
          className="btn btn--full btn--outline"
          onClick={() => setSpectrumFormOpen(!spectrumFormOpen)}
        >
          {spectrumFormOpen ? t('lobby.cancel') : t('lobby.submitSpectrum')}
        </button>

        {spectrumFormOpen && (
          <div className="spectrum-form">
            <p className="spectrum-form-description">
              {t('lobby.spectrumFormDescription')}
            </p>
            <div className="spectrum-input-row">
              <input
                type="text"
                className="input spectrum-input"
                placeholder={t('lobby.spectrumLeftPlaceholder')}
                value={spectrumLeft}
                onChange={(e) => setSpectrumLeft(e.target.value)}
                maxLength={50}
              />
              <span className="spectrum-separator">↔</span>
              <input
                type="text"
                className="input spectrum-input"
                placeholder={t('lobby.spectrumRightPlaceholder')}
                value={spectrumRight}
                onChange={(e) => setSpectrumRight(e.target.value)}
                maxLength={50}
              />
            </div>
            <button
              className="btn btn--full btn--primary"
              onClick={handleSpectrumSubmit}
              disabled={!spectrumLeft.trim() || !spectrumRight.trim()}
            >
              {t('lobby.submitSpectrumBtn')}
            </button>
          </div>
        )}
      </div>

      <button className="btn btn--full btn--outline-danger" style={{ marginTop: '0.75rem' }} onClick={onLeaveRoom}>
        {t('lobby.leaveRoom')}
      </button>
    </div>
  );
}

export const LobbyScreen = memo(LobbyScreenInner);
